import { useState, useEffect, useCallback, type FormEvent } from 'react';
import { api } from '../api/public';
import s from './TrialSignup.module.css';

interface Props {
  open: boolean;
  onClose: () => void;
}

type Step = 1 | 2 | 3 | 4 | 5; // 5 = success

export function TrialSignup({ open, onClose }: Props) {
  const [step, setStep] = useState<Step>(1);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [company, setCompany] = useState({ company_name: '', slug: '', license_number: '', tel: '' });
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);

  // Reset when closed
  useEffect(() => {
    if (!open) {
      setStep(1);
      setEmail('');
      setCode('');
      setCompany({ company_name: '', slug: '', license_number: '', tel: '' });
      setPassword('');
      setConfirmPassword('');
      setError('');
      setCooldown(0);
    }
  }, [open]);

  // Cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleSendCode = useCallback(async (e?: FormEvent) => {
    e?.preventDefault();
    setLoading(true);
    setError('');
    try {
      await api.sendVerifyCode(email);
      setStep(2);
      setCooldown(60);
    } catch (err) {
      setError(err instanceof Error ? err.message : '送信に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [email]);

  const handleVerifyCode = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await api.confirmVerifyCode(email, code);
      if (res.verified) {
        setStep(3);
      } else {
        setError('認証コードが正しくありません');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '認証に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleCompanySubmit = (e: FormEvent) => {
    e.preventDefault();
    setStep(4);
  };

  const handleRegister = async (e: FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      setError('パスワードは8文字以上で入力してください');
      return;
    }
    if (password !== confirmPassword) {
      setError('パスワードが一致しません');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await api.registerTrial({
        email,
        code,
        password,
        ...company,
      });
      setStep(5);
    } catch (err) {
      setError(err instanceof Error ? err.message : '登録に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (cooldown > 0) return;
    await handleSendCode();
  };

  if (!open) return null;

  const stepLabels = ['メール入力', '認証', '会社情報', 'パスワード'];

  return (
    <div className={s.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={s.modal}>
        <button className={s.close} onClick={onClose} aria-label="閉じる">&#10005;</button>
        <h2 className={s.title}>無料トライアル</h2>

        {step < 5 && (
          <>
            <p className={s.stepIndicator}>ステップ {step} / 4 - {stepLabels[step - 1]}</p>
            <div className={s.steps}>
              {[1, 2, 3, 4].map((n) => (
                <div
                  key={n}
                  className={`${s.stepDot} ${n === step ? s.stepDotActive : ''} ${n < step ? s.stepDotDone : ''}`}
                />
              ))}
            </div>
          </>
        )}

        {error && <div className={s.error}>{error}</div>}

        {step === 1 && (
          <form className={s.form} onSubmit={handleSendCode}>
            <div className={s.field}>
              <label className={s.label}>メールアドレス</label>
              <input
                className={s.input}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@company.jp"
                required
                autoFocus
              />
            </div>
            <button className={s.btn} type="submit" disabled={loading}>
              {loading ? '送信中...' : '認証コードを送信'}
            </button>
          </form>
        )}

        {step === 2 && (
          <form className={s.form} onSubmit={handleVerifyCode}>
            <p className={s.hint}>{email} に6桁の認証コードを送信しました。</p>
            <div className={s.field}>
              <label className={s.label}>認証コード</label>
              <input
                className={s.codeInput}
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                required
                autoFocus
              />
            </div>
            <button className={s.btn} type="submit" disabled={loading || code.length !== 6}>
              {loading ? '認証中...' : '認証する'}
            </button>
            <button type="button" className={s.resend} onClick={resend} disabled={cooldown > 0}>
              {cooldown > 0 ? `再送信まで ${cooldown}秒` : 'コードを再送信'}
            </button>
          </form>
        )}

        {step === 3 && (
          <form className={s.form} onSubmit={handleCompanySubmit}>
            <div className={s.field}>
              <label className={s.label}>会社名</label>
              <input
                className={s.input}
                type="text"
                value={company.company_name}
                onChange={(e) => setCompany((c) => ({ ...c, company_name: e.target.value }))}
                required
                autoFocus
              />
            </div>
            <div className={s.field}>
              <label className={s.label}>テナントID（英数字・ハイフン）</label>
              <input
                className={s.input}
                type="text"
                value={company.slug}
                onChange={(e) => setCompany((c) => ({ ...c, slug: e.target.value.replace(/[^a-z0-9-]/g, '') }))}
                placeholder="my-company"
                required
              />
            </div>
            <div className={s.field}>
              <label className={s.label}>宅建業免許番号<span className={s.labelOptional}>（任意）</span></label>
              <input
                className={s.input}
                type="text"
                value={company.license_number}
                onChange={(e) => setCompany((c) => ({ ...c, license_number: e.target.value }))}
              />
            </div>
            <div className={s.field}>
              <label className={s.label}>電話番号<span className={s.labelOptional}>（任意）</span></label>
              <input
                className={s.input}
                type="tel"
                value={company.tel}
                onChange={(e) => setCompany((c) => ({ ...c, tel: e.target.value }))}
              />
            </div>
            <button className={s.btn} type="submit">次へ</button>
          </form>
        )}

        {step === 4 && (
          <form className={s.form} onSubmit={handleRegister}>
            <div className={s.field}>
              <label className={s.label}>パスワード</label>
              <input
                className={s.input}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
                required
                autoFocus
              />
              <span className={s.passwordRules}>8文字以上</span>
            </div>
            <div className={s.field}>
              <label className={s.label}>パスワード（確認）</label>
              <input
                className={s.input}
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                minLength={8}
                required
              />
            </div>
            <button className={s.btn} type="submit" disabled={loading}>
              {loading ? '送信中...' : '申込'}
            </button>
          </form>
        )}

        {step === 5 && (
          <div className={s.success}>
            <span className={s.successIcon}>&#10003;</span>
            <h3 className={s.successTitle}>お申し込みありがとうございます</h3>
            <p className={s.successDesc}>
              審査完了後、ログインURLをメールでお送りします。<br />
              通常1〜2営業日以内にご案内いたします。
            </p>
            <button className={s.btn} onClick={onClose} style={{ marginTop: 16 }}>閉じる</button>
          </div>
        )}
      </div>
    </div>
  );
}
